import React, { useEffect, useState } from 'react';
import { Search, Filter, ChevronDown, Heart, Star, Users, Zap, Trophy, Flame, Clock, Video, Image as ImageIcon, Sparkles, ChevronLeft, ChevronRight, MoreVertical, Grid, BellPlus, Eye } from 'lucide-react';
import { UncoverAngelCard } from './UncoverAngelCard';
import { TagFilterBar } from './TagFilterBar';
import { UncoverGrid } from './uncover/UncoverGrid';
import { ResponsiveImage } from './uncover/ResponsiveImage';
interface FilteredDiscoveryFeedProps {
  onAngelSelect: (angelId: string) => void;
}
export const FilteredDiscoveryFeed: React.FC<FilteredDiscoveryFeedProps> = ({
  onAngelSelect
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('trending');
  const [showFilterOptions, setShowFilterOptions] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [gridSize, setGridSize] = useState<'compact' | 'standard' | 'immersive'>('standard');
  const [isPersonalizedSticky, setIsPersonalizedSticky] = useState(false);
  // Handle scroll for sticky personalized section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsPersonalizedSticky(scrollPosition > 150);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  // Filter tabs
  const filterTabs = [{
    id: 'for-you',
    label: 'For You',
    icon: <Flame size={16} className="text-pink-500" data-id="element-7645" />
  }, {
    id: 'trending',
    label: 'Trending',
    icon: <Sparkles size={16} className="text-blue-500" data-id="element-7646" />
  }, {
    id: 'online',
    label: 'Online',
    icon: <Zap size={16} className="text-green-500" data-id="element-7647" />
  }, {
    id: 'new',
    label: 'New',
    icon: <Star size={16} className="text-yellow-500" data-id="element-7648" />
  }];
  // Category tags
  const categoryTags = ['Fitness', 'Yoga', 'Meditation', 'Cooking', 'Travel', 'Gaming', 'Naughty', 'Playful'];
  const handleTagChange = (tags: string[]) => {
    setSelectedTags(tags);
  };
  return <div className="w-full flex justify-center" data-id="element-7649">
      <div className="max-w-7xl w-full space-y-6" data-id="element-7650">
        {/* Header with filters and search */}
        <div className="sticky top-0 z-10 bg-white/95 dark:bg-gray-900/95 backdrop-blur-md pt-2 pb-2 px-2 -mx-2 border-b border-gray-200/50 dark:border-gray-800/50" data-id="element-7651">
          <div className="flex justify-between items-center mb-3 ml-2 pt-2 pb-2" data-id="element-7652">
            <h2 className="text-lg font-medium tracking-tight text-neutral-500 dark:text-white" data-id="element-7653">
              Uncover Angels
            </h2>
            {/* Grid size toggle */}
            <div className="flex items-center gap-2 bg-gray-100 dark:bg-gray-800 rounded-lg p-1" data-id="element-7654">
              <button onClick={() => setGridSize('compact')} className={`p-1.5 rounded-md transition-colors ${gridSize === 'compact' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'}`} title="Compact View" data-id="element-7655">
                <div size={16} data-id="element-7656" />
              </button>
              <button onClick={() => setGridSize('standard')} className={`p-1.5 rounded-md transition-colors ${gridSize === 'standard' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'}`} title="Standard View" data-id="element-7657">
                <div size={16} data-id="element-7658" />
              </button>
              <button onClick={() => setGridSize('immersive')} className={`p-1.5 rounded-md transition-colors ${gridSize === 'immersive' ? 'bg-gray-200 dark:bg-gray-700 text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-300'}`} title="Immersive View" data-id="element-7659">
                <Grid size={16} data-id="element-7660" />
              </button>
            </div>
          </div>
          {/* Filter tabs with updated styling */}
          <div className="relative mb-3" data-id="element-7661">
            <div className="flex overflow-x-auto pb-2 scrollbar-hide" data-id="element-7662">
              <div className="flex space-x-2" data-id="element-7663">
                {filterTabs.map(tab => <button key={tab.id} onClick={() => setActiveFilter(tab.id)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg whitespace-nowrap transition-all duration-200 text-xs ${activeFilter === tab.id ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:shadow-[inset_0_0_4px_rgba(255,255,255,0.2)]'}`} data-id="element-7664">
                    {tab.icon}
                    <span className="text-[13px]" data-id="element-7665">{tab.label}</span>
                  </button>)}
              </div>
            </div>
          </div>
          {/* Category filters with updated styling */}
          <div className="overflow-x-auto scrollbar-hide pb-2" data-id="element-7666">
            <div className="flex space-x-2" data-id="element-7667">
              {categoryTags.map(tag => <button key={tag} onClick={() => {
              if (selectedTags.includes(tag)) {
                setSelectedTags(selectedTags.filter(t => t !== tag));
              } else {
                setSelectedTags([...selectedTags, tag]);
              }
            }} className={`px-3 py-1.5 rounded-lg text-[13px] whitespace-nowrap transition-all duration-200 ${selectedTags.includes(tag) ? 'bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium' : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 hover:shadow-[inset_0_0_4px_rgba(255,255,255,0.2)]'}`} data-id="element-7668">
                  {tag}
                </button>)}
            </div>
          </div>
          {/* Search field with updated styling */}
          <div className="relative mt-3" data-id="element-7669"></div>
        </div>
        {/* Personalized Section - updated styling */}
        <div className={`${isPersonalizedSticky ? 'sticky top-32 z-10 transform transition-all duration-300 translate-y-0 opacity-100' : 'relative transform transition-all duration-300'} bg-gradient-to-r from-[#f7f2fc] to-[#f2f2f7] dark:from-gray-900/80 dark:to-purple-900/30 rounded-[14px] p-[10px_16px] mb-6 border border-gray-200/50 dark:border-purple-900/30 shadow-[inset_0_0_0.5px_rgba(255,255,255,0.3)]`} data-id="element-7670">
          <div className="flex items-center gap-2 mb-1" data-id="element-7671">
            <Sparkles size={16} className="text-pink-500" data-id="element-7672" />
            <h3 className="text-[13px] font-medium text-gray-700 dark:text-white" data-id="element-7673">
              Personalized For You
            </h3>
          </div>
          <p className="text-[13px] text-gray-500 dark:text-gray-400 font-normal leading-[1.4]" data-id="element-7674">
            Based on your preferences, we've curated angels we think you'll
            love. Like or dislike to improve recommendations.
          </p>
        </div>
        {/* Instagram-style Uncover Grid with responsive layout */}
        <UncoverGrid onAngelSelect={onAngelSelect} gridSize={gridSize} data-id="element-7675" />
        <style jsx data-id="element-7676">{`
          .scrollbar-hide::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-hide {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}</style>
      </div>
    </div>;
};